import React from 'react';

export type PagedSelectResult<TOption> = {
  list: TOption[];
  total: number;
  page: number;
  pageSize: number;
};

type PagedSelectCacheEntry<TOption> = {
  options: TOption[];
  page: number;
  hasMore: boolean;
  loaded: boolean;
};

type FailedLoad = {
  keyword: string;
  page: number;
  append: boolean;
};

type UsePagedSelectOptionsParams<TOption> = {
  loadPage: (params: {
    keyword: string;
    page: number;
    pageSize: number;
  }) => Promise<PagedSelectResult<TOption>>;
  pageSize?: number;
  debounceMs?: number;
  cacheKey?: string;
};

const emptyEntry = <TOption>(): PagedSelectCacheEntry<TOption> => ({
  options: [],
  page: 0,
  hasMore: true,
  loaded: false,
});

const normalizeKeyword = (value: string) => value.trim();

export function usePagedSelectOptions<
  TOption extends { value: string | number },
>({
  loadPage,
  pageSize = 20,
  debounceMs = 300,
  cacheKey = 'default',
}: UsePagedSelectOptionsParams<TOption>) {
  const cacheRef = React.useRef(
    new Map<string, PagedSelectCacheEntry<TOption>>(),
  );
  const requestIdsRef = React.useRef(new Map<string, number>());
  const loadingKeysRef = React.useRef(new Set<string>());
  const generationRef = React.useRef(0);
  const failedLoadRef = React.useRef<FailedLoad | undefined>(undefined);
  const keywordRef = React.useRef('');
  const loadPageRef = React.useRef(loadPage);
  const searchTimerRef = React.useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  const mountedRef = React.useRef(true);
  const [keyword, setKeyword] = React.useState('');
  const [revision, setRevision] = React.useState(0);
  const [loadingInitial, setLoadingInitial] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [initialError, setInitialError] = React.useState(false);
  const [loadMoreError, setLoadMoreError] = React.useState(false);

  loadPageRef.current = loadPage;

  const getEntry = React.useCallback((targetKeyword: string) => {
    const cached = cacheRef.current.get(targetKeyword);
    if (cached) return cached;
    const entry = emptyEntry<TOption>();
    cacheRef.current.set(targetKeyword, entry);
    return entry;
  }, []);

  const notifyCurrentKeyword = React.useCallback((targetKeyword: string) => {
    if (keywordRef.current === targetKeyword && mountedRef.current) {
      setRevision((current) => current + 1);
    }
  }, []);

  const requestPage = React.useCallback(
    async (targetKeyword: string, page: number, append: boolean) => {
      if (loadingKeysRef.current.has(targetKeyword)) return;
      const generation = generationRef.current;
      const requestId = (requestIdsRef.current.get(targetKeyword) || 0) + 1;
      requestIdsRef.current.set(targetKeyword, requestId);
      loadingKeysRef.current.add(targetKeyword);
      failedLoadRef.current = undefined;
      if (keywordRef.current === targetKeyword) {
        setInitialError(false);
        setLoadMoreError(false);
        if (append) setLoadingMore(true);
        else setLoadingInitial(true);
      }

      try {
        const result = await loadPageRef.current({
          keyword: targetKeyword,
          page,
          pageSize,
        });
        if (
          generationRef.current !== generation ||
          requestIdsRef.current.get(targetKeyword) !== requestId
        ) {
          return;
        }
        const current = getEntry(targetKeyword);
        const merged = new Map<string | number, TOption>();
        if (append) {
          current.options.forEach((option) => {
            merged.set(option.value, option);
          });
        }
        result.list.forEach((option) => {
          merged.set(option.value, option);
        });
        cacheRef.current.set(targetKeyword, {
          options: Array.from(merged.values()),
          page: result.page || page,
          hasMore:
            (result.page || page) * result.pageSize < Number(result.total || 0),
          loaded: true,
        });
        notifyCurrentKeyword(targetKeyword);
      } catch {
        if (
          generationRef.current !== generation ||
          requestIdsRef.current.get(targetKeyword) !== requestId
        ) {
          return;
        }
        failedLoadRef.current = { keyword: targetKeyword, page, append };
        if (keywordRef.current === targetKeyword && mountedRef.current) {
          if (append) setLoadMoreError(true);
          else setInitialError(true);
        }
      } finally {
        if (
          generationRef.current === generation &&
          requestIdsRef.current.get(targetKeyword) === requestId
        ) {
          loadingKeysRef.current.delete(targetKeyword);
          if (keywordRef.current === targetKeyword && mountedRef.current) {
            setLoadingInitial(false);
            setLoadingMore(false);
          }
        }
      }
    },
    [getEntry, notifyCurrentKeyword, pageSize],
  );

  const ensureLoaded = React.useCallback(() => {
    const currentKeyword = keywordRef.current;
    const entry = getEntry(currentKeyword);
    if (entry.loaded || loadingKeysRef.current.has(currentKeyword)) {
      return Promise.resolve();
    }
    return requestPage(currentKeyword, 1, false);
  }, [getEntry, requestPage]);

  const loadMore = React.useCallback(() => {
    const currentKeyword = keywordRef.current;
    const entry = getEntry(currentKeyword);
    if (
      !entry.loaded ||
      !entry.hasMore ||
      loadingKeysRef.current.has(currentKeyword)
    ) {
      return Promise.resolve();
    }
    return requestPage(currentKeyword, entry.page + 1, true);
  }, [getEntry, requestPage]);

  const retry = React.useCallback(() => {
    const failedLoad = failedLoadRef.current;
    if (!failedLoad || failedLoad.keyword !== keywordRef.current) {
      return ensureLoaded();
    }
    return requestPage(failedLoad.keyword, failedLoad.page, failedLoad.append);
  }, [ensureLoaded, requestPage]);

  const search = React.useCallback(
    (value: string) => {
      const nextKeyword = normalizeKeyword(value);
      keywordRef.current = nextKeyword;
      setKeyword(nextKeyword);
      setLoadingInitial(false);
      setLoadingMore(false);
      setInitialError(false);
      setLoadMoreError(false);
      failedLoadRef.current = undefined;
      setRevision((current) => current + 1);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (getEntry(nextKeyword).loaded) return;
      searchTimerRef.current = setTimeout(() => {
        if (
          getEntry(nextKeyword).loaded ||
          loadingKeysRef.current.has(nextKeyword)
        ) {
          return;
        }
        void requestPage(nextKeyword, 1, false);
      }, debounceMs);
    },
    [debounceMs, getEntry, requestPage],
  );

  React.useEffect(() => {
    generationRef.current += 1;
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = undefined;
    }
    cacheRef.current.clear();
    loadingKeysRef.current.clear();
    failedLoadRef.current = undefined;
    keywordRef.current = '';
    setKeyword('');
    setLoadingInitial(false);
    setLoadingMore(false);
    setInitialError(false);
    setLoadMoreError(false);
    setRevision((current) => current + 1);
  }, [cacheKey]);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  const entry = getEntry(keyword);
  return {
    ensureLoaded,
    hasMore: entry.hasMore,
    initialError,
    keyword,
    loadMore,
    loadMoreError,
    loading: loadingInitial || loadingMore,
    loadingInitial,
    loadingMore,
    options: entry.options,
    retry,
    search,
    revision,
  };
}
